import { ReactNode } from 'react';

import { initials, PersonAvatar } from 'components/person-avatar';
import { FOCO_VISIVEL } from 'utils/foco';

import { rostoDoParticipante } from '../identidade-do-participante';
import { HUMANOGRAMA } from './AgendaDaRealizacao';

/** Mesmos textos do `TaskItem` para o status da quantidade de participantes. */
export const STATUS_DA_PARTICIPACAO: Record<string, string> = {
  autoApproved: 'Número de participantes liberados automaticamente',
  pending: 'Número de participantes pendentes de autorização',
  approved: 'Número de participantes aprovados',
  rejected: 'Número de participantes não aprovados',
};

/** Avaliador automático do sistema: o `TaskItem` não mostra a etapa no rótulo do feedback dele. */
const AVALIADOR_AUTOMATICO = 'F0000000';

export interface DetalhesDaRealizacaoProps {
  data: any;
  getUserTeam: (chave: string) => any;
  getFormattedDate: (valor: any) => string;
  getStepLabel: (etapa: number) => string;
  /** Sem o percentual de atuação (fluxo ECOA), como no `TaskItem`. */
  ocultarPercentual?: boolean;
  /** Ações da linha de um participante (Reduzir/Excluir do `TaskItem`), quando o cartão as oferece. */
  acoesDoParticipante?: (pessoa: any) => ReactNode;
}

function Campo({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="mb-1 text-[11px] text-content">{rotulo}</dt>
      <dd className="m-0 text-xs leading-[1.6] text-content">{children}</dd>
    </div>
  );
}

function rotuloDoFeedback(feedback: any, getStepLabel: (etapa: number) => string): string {
  const etapa = getStepLabel(feedback.etapa);
  const verbo = etapa === 'Validação' ? 'Avaliado' : 'Apreciado';
  const prefixo = feedback.evaluatorId !== AVALIADOR_AUTOMATICO && etapa.length ? `${etapa}: ` : '';
  return `${prefixo}${verbo} por ${feedback.evaluator_nome_guerra}`;
}

const porNome = (a: any, b: any) => (a?.name < b?.name ? -1 : a?.name > b?.name ? 1 : 0);

/** Conteúdo de "Ver mais informações" (RDN-09): os mesmos campos do "Ver mais" do `TaskItem`. */
export default function DetalhesDaRealizacao({
  data,
  getUserTeam,
  getFormattedDate,
  getStepLabel,
  ocultarPercentual,
  acoesDoParticipante,
}: DetalhesDaRealizacaoProps) {
  const feedbacks = Array.isArray(data.feedback) ? [...data.feedback].sort((a, b) => b.etapa - a.etapa) : [];
  const participantes = Array.isArray(data.target) ? [...data.target].sort(porNome) : [];
  const statusDaParticipacao = data.participants_approval?.status;

  return (
    <div className="mt-4 flex flex-col gap-3.5 rounded-jj-lg bg-surface-100 px-5 py-[18px]">
      <dl className="m-0 grid grid-cols-1 gap-3.5 md:grid-cols-2">
        {!!data.indicator?.nome && <Campo rotulo="Indicador">{data.indicator.nome}</Campo>}
        {!!data.nec?.cd_alcc && !!data.nec?.nm_uor_sltt && (
          <Campo rotulo="NEC/Iniciativa">{`${data.nec.cd_alcc} - ${data.nec.nm_alcc}`}</Campo>
        )}
        {typeof data.nec === 'string' && !!data.nec.length && <Campo rotulo="NEC/Iniciativa">{data.nec}</Campo>}
        <Campo rotulo="Período da entrega">{`${getFormattedDate(data.start)} a ${getFormattedDate(data.end)}`}</Campo>
        {!!data.nec?.cd_alcc && !!data.nec?.nm_uor_sltt && <Campo rotulo="Demandante">{data.nec.nm_uor_sltt}</Campo>}
        {!!statusDaParticipacao && (
          <Campo rotulo="Status de participação">{STATUS_DA_PARTICIPACAO[statusDaParticipacao]}</Campo>
        )}
      </dl>

      <dl className="m-0 flex flex-col gap-3.5 border-t border-surface-200 pt-3.5">
        <Campo rotulo="Detalhes da realização">
          <span className="whitespace-pre-wrap">{typeof data.description === 'string' ? data.description : ''}</span>
        </Campo>
        <Campo rotulo="Resultados da realização">
          <span className="whitespace-pre-wrap">{typeof data.result === 'string' ? data.result : ''}</span>
        </Campo>
      </dl>

      {feedbacks.length > 0 && (
        <section aria-label="Feedbacks" className="border-t border-surface-200 pt-3.5">
          <h4 className="m-0 mb-2 text-[11px] font-normal text-content">Feedbacks</h4>
          <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
            {feedbacks.map(feedback => (
              <li key={feedback._id} className="text-xs leading-[1.6] text-content">
                <p className="m-0 font-bold">{rotuloDoFeedback(feedback, getStepLabel)}</p>
                {/* Mesmo HTML que o `TaskItem` renderiza para a mensagem do feedback. */}
                <p className="m-0 italic" dangerouslySetInnerHTML={{ __html: feedback.message }} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {participantes.length > 0 && (
        <section aria-label="Participantes da realização" className="border-t border-surface-200 pt-3.5">
          <h4 className="m-0 mb-2 text-xs font-bold text-brand-blue-dark">{`Participantes da realização (${participantes.length})`}</h4>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {participantes.map(pessoa => {
              const membro = getUserTeam(pessoa._id);
              return (
                <li key={pessoa._id} data-participante={pessoa._id} className="rounded-jj-md bg-brand-white px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2.5">
                    {/* Avatar montado, foto ou iniciais, como o `UserAvatar` da linha no `TaskItem`. */}
                    <PersonAvatar
                      {...rostoDoParticipante(pessoa)}
                      initials={initials(pessoa.extra?.nome_guerra || pessoa._id)}
                      size={36}
                      initialsClassName="bg-brand-green font-display text-[11px] font-bold text-brand-navy"
                    />
                    <div className="min-w-0 flex-1">
                      <a
                        href={`${HUMANOGRAMA}${pessoa._id}`}
                        target="_blank"
                        rel="noreferrer"
                        className={`text-xs font-bold text-link hover:text-link-hover ${FOCO_VISIVEL}`}
                      >
                        {`${pessoa.extra?.nome_guerra?.split(' ')[0] ?? ''} (${pessoa._id})`}
                      </a>
                      <p className="m-0 mt-0.5 text-[11px] text-content-soft">
                        {`${pessoa.extra?.funcao ?? ''} - ${pessoa.extra?.prefixo ?? ''} - ${pessoa.extra?.nome_dependencia ?? ''}`}
                      </p>
                    </div>
                    {!ocultarPercentual && (
                      <span className="rounded-jj-pill bg-brand-green px-3 py-1 font-display text-[11px] font-bold text-brand-navy">
                        {`${membro?.percent || '0'}% de atuação`}
                      </span>
                    )}
                    {acoesDoParticipante?.(pessoa)}
                  </div>
                  {!!membro?.message && (
                    <div className="mt-2">
                      <p className="m-0 mb-0.5 text-[11px] text-content-soft">Atuação desse funcionário</p>
                      {/* Mesmo HTML que o `TaskItem` renderiza para a descrição de atuação. */}
                      <div className="text-xs text-content" dangerouslySetInnerHTML={{ __html: membro.message }} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}
